import { createSlice } from "@reduxjs/toolkit"
import { RootState } from "../app/store"
import { uploadHistory, UploadLog } from "../models/uploadHistory.model"

export type PaginationType = {
  page: number
  pageSize: number
}

type UploadHistoryState = {
  uploadHistory: uploadHistory[]
  uploadLog: UploadLog
  loading: boolean
  pageInfo: {
    totalRowCount: number
  }
  filters: {
    bankAccountId?: string[]
    status?: string
    sortBy: string
    orderBy: string
  }
  pagination: PaginationType
  filterCount: number
}

const initialPaginationState = {
  page: 0,
  pageSize: 10,
}

export const uploadHistoryInitialState: UploadHistoryState = {
  uploadHistory: [],
  uploadLog: new UploadLog(),
  loading: false,
  pageInfo: {
    totalRowCount: 0,
  },
  filters: {
    bankAccountId: [],
    status: "",
    sortBy: "uploaded_at",
    orderBy: "desc",
  },
  pagination: initialPaginationState,
  filterCount: 0,
}

export type fetchuploadHistoryPayloadType = {
  payload: {
    page: number
    pageSize: number
    bankAccountId?: string[]
    status?: string
    sortBy?: string
    orderBy?: string
  }
}
export type fetchuploadLogPayloadType = {
  payload: {
    bankAccountId?: string
  }
}
export type fetchBankStatementPayloadType = {
  payload: {
    id: string
    name: string
  }
}

export const uploadHistorySlice = createSlice({
  name: "uploadHistory",
  initialState: uploadHistoryInitialState,
  reducers: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchUploadHistory: (state, action: fetchuploadHistoryPayloadType) => {
      state.loading = true
    },
    fetchUploadHistorySuccess: (
      state,
      {
        payload,
      }: { payload: { uploadHistory: uploadHistory[]; totalRowCount: number } },
    ) => {
      state.pageInfo.totalRowCount = payload.totalRowCount
      state.uploadHistory = payload.uploadHistory
      state.loading = false
    },
    fetchUploadHistoryFailed: (state) => {
      state.loading = false
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchUploadlog: (state, action: fetchuploadLogPayloadType) => {
      state.loading = true
    },
    fetchUploadlogSuccess: (
      state,
      { payload }: { payload: { uploadLog: UploadLog } },
    ) => {
      state.uploadLog = payload.uploadLog
      state.loading = false
    },
    fetchUploadlogFailed: (state) => {
      state.loading = false
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fetchBankStatement: (state, action: fetchBankStatementPayloadType) => {
      state.loading = true
    },
    fetchBankStatementSuccess: (state) => {
      state.loading = false
    },
    fetchBankStatementFailed: (state) => {
      state.loading = false
    },
    setPagination: (state, { payload }) => {
      state.pagination = payload
    },
    setFilters: (state, { payload }) => {
      state.filters = payload
      state.pagination = initialPaginationState
    },
    setFiltersFromURL: (state, { payload }) => {
      state.filters = payload
    },
    resetFilters: (state) => {
      state.filters = uploadHistoryInitialState.filters
      state.filterCount = 0
      state.pagination = initialPaginationState
    },
    setFilterCount: (state) => {
      state.filterCount = Object.keys(state.filters ?? {}).filter(
        (key: string) => {
          const filterKeys = ["bankAccountId", "status"]
          if (
            key === "bankAccountId" &&
            Array.isArray(state.filters.bankAccountId)
          ) {
            return (state.filters.bankAccountId as string[]).some(
              (id: string) =>
                !uploadHistoryInitialState?.filters?.bankAccountId?.includes(
                  id,
                ),
            )
          }
          return (
            filterKeys.includes(key) &&
            state.filters[key as keyof typeof state.filters] !==
              uploadHistoryInitialState?.filters?.[
                key as keyof typeof state.filters
              ]
          )
        },
      ).length
    },
  },
})

export const {
  fetchUploadHistory,
  fetchUploadHistorySuccess,
  fetchUploadHistoryFailed,
  fetchUploadlog,
  fetchUploadlogSuccess,
  fetchUploadlogFailed,
  fetchBankStatement,
  fetchBankStatementSuccess,
  fetchBankStatementFailed,
  setPagination,
  setFilters,
  resetFilters,
  setFilterCount,
  setFiltersFromURL,
} = uploadHistorySlice.actions

export const selectUploadHistory = (state: RootState) => state.uploadHistory

export default uploadHistorySlice.reducer
